import React, {useContext, useState} from 'react'
import PropTypes from 'prop-types'

import {
    Button,
    Heading,
    Box,
    Text,
    Stack,
    Flex,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import useMultiSite from '@app/hooks/use-multi-site'
import {useSplioService} from '@app/hooks/use-splio-service.js'
import colors from '@app/theme/foundations/colors.js'
import FieldsetForm from '@app/components/shared/ui/FieldsetForm/index.jsx'
import {useProfile} from '@app/hooks/use-update-profile-attributes.js'
import {IPContext} from '@app/custom-contexts'
import {useHistory} from 'react-router-dom'
import {useXSSProtection} from '@app/hooks/use-xss-protection.js'
import Field from '../../../components/field'
import useOutletSubscriptionFields from '../../../components/forms/useOutletSubscriptionFields'
import {BrandLogo} from '../../../components/custom-icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import {HOME_HREF, OUTLET_DISPATCH_URL, PRIVACY_POLICY_URL} from '../../../constants'

/**
 * Renders the OutletSubscription component, which provides a user interface for subscribing to notifications about the upcoming e-outlet opening.
 *
 * This component manages the entire subscription flow, including form handling, validation, and submission logic. It integrates with various services and contexts to:
 * - Validate user input and protect against XSS attacks.
 * - Check if the user's email is blacklisted or if the user's IP is blocked.
 * - Forward valid subscription requests to the Splio service.
 * - Update user profile consent attributes upon successful subscription.
 * - Display appropriate feedback messages and UI states based on the subscription outcome.
 *
 * The UI includes branding, dynamic headings and messages, a subscription form with email and consent fields, privacy policy acknowledgment, and navigation links.
 * The component uses hooks for state management, internationalization, routing, and form handling, ensuring a responsive and accessible user experience.
 *
 * @component
 * @returns {JSX.Element} The rendered OutletSubscription component.
 */
export const OutletSubscription = ({isGetCode, setIsGetCode, setIsPrefilled}) => {
    const {updateProfileConsentAttributes} = useProfile()
    const {site, locale, buildUrl} = useMultiSite()
    const {isBlackListedEmail, forwardAccountToSplio} = useSplioService()
    const {formatMessage} = useIntl()
    const form = useForm()
    const {isIPBlocked} = useContext(IPContext)
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()
    const fields = useOutletSubscriptionFields({form})
    const [isSubmitted, setIsSubmitted] = useState(false)

    /**
     * Handles the outlet subscription process, including validation, blacklist checking, and forwarding account details.
     *
     * This asynchronous function processes the outlet subscription form submission. It performs the following steps:
     * 1. Checks if the user's IP is blocked and redirects to the homepage if so.
     * 2. Validates the presence of an email address in the form data.
     * 3. Performs XSS validation on the form data and aborts if any issues are detected.
     * 4. Constructs a request object with the user's email, site, and locale information.
     * 5. Checks if the provided email is blacklisted:
     *    - If blacklisted, sets an error on the form with the appropriate message.
     *    - If not blacklisted, forwards the account details to Splio, updates profile consent attributes, and marks the form as submitted.
     *
     * @async
     * @function handleOutletSubscription
     * @param {Object} data - The form data containing the outlet subscription email.
     * @returns {Promise<void>} Resolves when the subscription process is complete or aborted due to validation.
     */
    const handleOutletSubscription = async (data) => {
        if (isIPBlocked) {
            const path = buildUrl(OUTLET_DISPATCH_URL)
            history.push(path)
            return
        }

        let email = data.outletsubscriptionemail

        if (!email) {
            return
        }

        const isXssDetected = validateXssForm(data)
        if (isXssDetected) {
            return
        }

        let requestObject = {
            email: email,
            siteId: site.id,
            locale: locale.id,
            requestType: 'outlet-subscription'
        }

        let blackListedResult = await isBlackListedEmail(requestObject)

        /**
         * Handles the result of the blacklist check for an email.
         *
         * If the email is blacklisted, it sets an error on the form. Otherwise, it forwards the account
         * details to Splio and displays a success message.
         */

        if (blackListedResult.success) {
            form.setError('outletsubscriptionemail', {
                type: 'blacklisted',
                message: blackListedResult.message
            })
        } else {
            await forwardAccountToSplio({
                email: email,
                firstName: '',
                lastName: '',
                phoneHome: '',
                marketingConsent: true,
                newsletterSection: 'body',
                requestType: 'outlet-subscription'
            })

            await updateProfileConsentAttributes(email)

            if (isGetCode) {
                setIsGetCode(false)
                setIsPrefilled(true)
            } else {
                setIsSubmitted(true)
            }
        }
    }

    return (
        <Flex
            maxWidth={'1100px'}
            padding={{base: '24px', lg: '24px 102px'}}
            margin={'auto'}
            backgroundColor={'white'}
            border={`1px solid ${colors.borderGray}`}
            justifyContent={'center'}
            flex={'1 0 100%'}
        >
            <Box maxWidth={'442px'} flex={1}>
                <Box textAlign={'center'}>
                    <BrandLogo width="94px" height="auto" mb={32} />
                    {!isSubmitted ? (
                        <>
                            <Heading as={'h1'} size={'4xl'} mb={'16px'} textTransform={'uppercase'}>
                                {isGetCode ? (
                                    <FormattedMessage
                                        defaultMessage="Subscribe to get your access code"
                                        id="outlet_subscription.form.getcode.title"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Our e-outlet opens soon!"
                                        id="outlet_subscription.form.soon.title"
                                    />
                                )}
                            </Heading>
                            {!isGetCode && (
                                <Text variant={'bodyBase2'} mb={'16px'}>
                                    <FormattedMessage
                                        defaultMessage="Stay with us!{br} Sign up to be notified of the opening."
                                        id="outlet_subscription.form.text"
                                        values={{br: <br />}}
                                    />
                                </Text>
                            )}
                        </>
                    ) : (
                        <>
                            <Heading as={'h1'} size={'4xl'} mb={'16px'} textTransform={'uppercase'}>
                                <FormattedMessage
                                    defaultMessage="Thank You!"
                                    id="outlet_subscription.confirmation.title"
                                />
                            </Heading>
                            <Text variant={'bodyBase2'}>
                                <FormattedMessage
                                    defaultMessage="Your subscription to our next outlet sale has been successfully taken into account.{br}{br}See you soon.{br}{br}The Vilebrequin Team"
                                    id="outlet_subscription.confirmation.text"
                                    values={{br: <br />}}
                                />
                            </Text>
                        </>
                    )}
                </Box>
                {!isSubmitted && (
                    <FieldsetForm
                        id="outlet-subscription-form"
                        data-testid="sf-auth-modal-form"
                        legendText={formatMessage({
                            defaultMessage: 'Outlet Subscription form',
                            id: 'outlet_subscription.form.legend'
                        })}
                        onSubmit={form.handleSubmit(handleOutletSubscription)}
                        error={form.formState.errors?.global?.message}
                        spacing={16}
                    >
                        <Stack spacing={16}>
                            <Field {...fields.email} />
                            <Field {...fields.consent} />
                        </Stack>

                        <Text variant={'bodyBase2'}>
                            <FormattedMessage
                                defaultMessage="By clicking “Submit”, you agree to our {privacypolicy}"
                                id="outlet_subscription.form.privacy_policy"
                                values={{
                                    privacypolicy: (
                                        <Link href={PRIVACY_POLICY_URL}>
                                            <Text as="span" variant={'bodyBase2'}>
                                                <FormattedMessage
                                                    defaultMessage="privacy policy"
                                                    id="outlet_subscription.form.privacy_policy.link"
                                                />
                                            </Text>
                                        </Link>
                                    )
                                }}
                            />
                        </Text>

                        <Button
                            type="submit"
                            onClick={() => {
                                form.clearErrors('global')
                            }}
                            isLoading={form.formState.isSubmitting}
                            variant="primary"
                        >
                            <FormattedMessage
                                defaultMessage="Submit"
                                id="outlet_subscription.form.button.label"
                            />
                        </Button>

                        {!isGetCode && (
                            <Box textAlign={'center'}>
                                <ChakraLink
                                    href={buildUrl(HOME_HREF, site?.alias?.replace('outlet-', ''))}
                                >
                                    <Text as="span" variant={'bodyBase1'}>
                                        <FormattedMessage
                                            defaultMessage="Discover the new collection"
                                            id="outlet_subscription.form.new_collection_link"
                                        />
                                    </Text>
                                </ChakraLink>
                            </Box>
                        )}
                    </FieldsetForm>
                )}
            </Box>
        </Flex>
    )
}

OutletSubscription.displayName = 'OutletSubscription'
OutletSubscription.propTypes = {
    isGetCode: PropTypes.bool,
    setIsGetCode: PropTypes.func,
    setIsPrefilled: PropTypes.func
}

export default OutletSubscription
